import { isMobileViewport, isTouchDevice } from '../utils/functions';

class MainHeader {
	header: HTMLDivElement | null;
	headerNav: HTMLDivElement | null;
	megaMenuItems: NodeListOf<HTMLElement> | null;
	megaMenuLinks: NodeListOf<HTMLAnchorElement> | null;
	megaMenuBackBtns: NodeListOf<HTMLButtonElement>;
	mobileMenuTrigger: HTMLButtonElement | null;
	mobileUtilityMenuItems: NodeListOf<HTMLElement> | null;
	adminBar: HTMLElement | null;
	profileMenus: NodeListOf<HTMLElement>;
	isMobile: boolean;
	isTouch: boolean;
	breakpoints: IBreakpoints | undefined;
	overlay: HTMLElement | null;
	removeOverlayTimeout: NodeJS.Timeout | null;
	focusableSelectors: string[];
	activeTrapFocusListener: ((e: KeyboardEvent) => void) | null;

	constructor() {
		this.header = document.querySelector<HTMLDivElement>('.main-header');
		this.headerNav = this.header?.querySelector<HTMLDivElement>('.main-header__nav') || null;
		this.adminBar = document.getElementById('wpadminbar');
		this.megaMenuItems =
			this.headerNav?.querySelectorAll(
				'.header-nav-menu__nav-item--has-mm-dropdown, .header-nav-menu__nav-item--has-dropdown'
			) || null;
		this.megaMenuLinks =
			this.headerNav?.querySelectorAll('.header-nav-menu__nav-item > a') || null;
		this.megaMenuBackBtns = document.querySelectorAll<HTMLButtonElement>(
			'button[data-mega-menu-back]'
		);
		this.mobileMenuTrigger = document.querySelector<HTMLButtonElement>('.mobile-menu-trigger');
		this.mobileUtilityMenuItems =
			this.headerNav?.querySelectorAll('.utility-nav-menu__nav-item--has-dropdown') || null;
		this.profileMenus = document.querySelectorAll('.profile-menu-dropdown');
		this.breakpoints = window.impBreakpoints;
		this.isMobile = false;
		this.isTouch = false;
		this.removeOverlayTimeout = null;
		this.overlay = document.querySelector('.main-header__overlay');
		this.activeTrapFocusListener = null;
		this.focusableSelectors = [
			'a[href]:not([tabindex="-1"])',
			'button:not([disabled]):not([tabindex="-1"])',
			'input:not([disabled]):not([tabindex="-1"])',
			'select:not([disabled]):not([tabindex="-1"])',
			'textarea:not([disabled]):not([tabindex="-1"])',
			'[tabindex]:not([tabindex="-1"])',
		];
	}

	init() {
		this.setHeaderHeight();

		if (this.megaMenuItems) {
			this.megaMenuItems.forEach((item) => {
				item.addEventListener(
					'mouseenter',
					this.handleMouseEnter.bind(this) as EventListener
				);
				item.addEventListener(
					'mouseleave',
					this.handleMouseLeave.bind(this) as EventListener
				);
				item
					.querySelector('a')
					?.addEventListener('click', this.handleMegaMenuClick.bind(this));
			});
		}

		if (this.megaMenuLinks) {
			this.megaMenuLinks.forEach((item) => {
				item.addEventListener('focusin', this.handleMouseEnter.bind(this) as EventListener);
				item.addEventListener('focusout', (e) => {
					const parentItem = item.closest('.header-nav-menu__nav-item--depth-1');
					if (parentItem) {
						if (!parentItem.contains(e.relatedTarget as HTMLElement)) {
							this.handleMouseLeave(e as unknown as MouseEvent);
						}
					}
				});
			});
		}

		if (this.mobileUtilityMenuItems) {
			this.mobileUtilityMenuItems.forEach((item) => {
				item
					.querySelector('a')
					?.addEventListener('click', this.handleMegaMenuClick.bind(this));
			});
		}

		if (this.profileMenus) {
			this.profileMenus.forEach((item) => {
				item.addEventListener('click', this.handleProfileMenuClick.bind(this));
			});
		}

		if (this.mobileMenuTrigger) {
			this.mobileMenuTrigger.addEventListener('click', this.handleMobileMenuClick.bind(this));
		}

		this.megaMenuBackBtns.forEach((item) => {
			item.addEventListener('click', this.handleBackBtnClick.bind(this));
		});

		if (this.breakpoints) {
			this.isMobile = isMobileViewport(this.breakpoints);
		}
		this.isTouch = isTouchDevice();
	}

	resized() {
		this.setHeaderHeight();
		if (this.breakpoints) {
			this.isMobile = isMobileViewport(this.breakpoints);
		}
		this.isTouch = isTouchDevice();

		if (this.isMobile) {
			document.body.classList.remove('pm-dropdown-open');
		}
	}

	loaded() {
		this.setHeaderHeight();
		if (this.breakpoints) {
			this.isMobile = isMobileViewport(this.breakpoints);
		}
		this.isTouch = isTouchDevice();
	}

	showOverlay() {
		if (this.removeOverlayTimeout) {
			clearTimeout(this.removeOverlayTimeout);
		}
		this.overlay?.classList.add('active');
	}

	removeOverlay() {
		if (this.removeOverlayTimeout) {
			clearTimeout(this.removeOverlayTimeout);
		}

		this.removeOverlayTimeout = setTimeout(() => {
			this.overlay?.classList.remove('active');
		}, 300);
	}

	handleMobileMenuClick() {
		if (this.isMobile) {
			this.profileMenus.forEach((menu) => {
				menu.classList.remove('is-open');
				const pmDropdown = menu.querySelector<HTMLElement>('.profile-menu-dropdown__menu');
				if (pmDropdown) {
					pmDropdown.classList.remove('is-open');
					document.body.classList.remove('pm-dropdown-open');
				}
			});
			if (!document.body.classList.contains('mobile-menu-open')) {
				document.body.classList.add('mobile-menu-open');
				document.body.classList.remove('mobile-mm-open');
				this.handleBackBtnClick();

				this.trapMobileMenu();
			} else {
				document.body.classList.remove('mobile-menu-open');
				document.body.classList.remove('mobile-mm-open');
				this.megaMenuBackBtns.forEach((btn) => {
					btn.classList.remove('is-open');
				});
				this.removeTrapFocus();
				this.enableAllNavElements();
			}
		}
	}

	trapFocus(
		container: HTMLElement,
		firstElement: HTMLElement,
		lastElement: HTMLElement,
		onEscape: () => void,
		returnFocusElement?: HTMLElement
	) {
		// Remove any existing trap focus listener
		this.removeTrapFocus();

		const trapFocusHandler = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onEscape();
				this.removeTrapFocus();
				if (returnFocusElement) {
					returnFocusElement.focus();
				}
				return;
			}

			if (e.key === 'Tab') {
				if (e.shiftKey && firstElement.ownerDocument.activeElement === firstElement) {
					e.preventDefault();
					lastElement.focus();
				}
				if (!e.shiftKey && lastElement.ownerDocument.activeElement === lastElement) {
					e.preventDefault();
					firstElement.focus();
				}
			}
		};

		this.activeTrapFocusListener = trapFocusHandler;
		container.addEventListener('keydown', trapFocusHandler);
	}

	removeTrapFocus() {
		if (this.activeTrapFocusListener && this.header) {
			this.header.removeEventListener('keydown', this.activeTrapFocusListener);
			this.activeTrapFocusListener = null;
		}
	}

	trapMobileMenu() {
		if (!this.headerNav || !this.mobileMenuTrigger || !this.header) return;

		const focusableElements = Array.from(
			this.headerNav.querySelectorAll(this.focusableSelectors.join(','))
		) as HTMLElement[];

		const firstEl = this.mobileMenuTrigger;
		const lastEl = focusableElements[focusableElements.length - 1];

		this.trapFocus(
			this.header,
			firstEl,
			lastEl,
			() => this.handleMobileMenuClick(),
			this.mobileMenuTrigger
		);
	}

	trapMegaMenu(megaMenuContainer: HTMLElement) {
		if (!this.header || !this.mobileMenuTrigger) return;

		const focusableElements = Array.from(
			megaMenuContainer.querySelectorAll(this.focusableSelectors.join(','))
		) as HTMLElement[];

		if (focusableElements.length === 0) return;

		const firstEl = this.mobileMenuTrigger;
		const lastEl = focusableElements[focusableElements.length - 1];

		this.disableOtherNavElements(megaMenuContainer);

		this.trapFocus(
			this.header,
			firstEl,
			lastEl,
			() => {
				// Close the entire mobile menu on escape, not just the mega menu
				this.handleMobileMenuClick();
			},
			this.mobileMenuTrigger
		);
	}

	disableOtherNavElements(activeMegaMenuContainer: HTMLElement) {
		if (!this.headerNav) return;

		const allNavFocusableElements = Array.from(
			this.headerNav.querySelectorAll(this.focusableSelectors.join(','))
		) as HTMLElement[];

		const activeMegaMenuElements = Array.from(
			activeMegaMenuContainer.querySelectorAll(this.focusableSelectors.join(','))
		) as HTMLElement[];

		// Disable elements that are not in the active mega menu and not the mobile menu trigger
		allNavFocusableElements.forEach((element) => {
			if (element !== this.mobileMenuTrigger && !activeMegaMenuElements.includes(element)) {
				element.setAttribute('tabindex', '-1');
				element.setAttribute('data-temp-disabled', 'true');
			}
		});
	}

	enableAllNavElements() {
		if (!this.headerNav) return;

		// Re-enable all temporarily disabled elements
		const disabledElements = this.headerNav.querySelectorAll('[data-temp-disabled="true"]');
		disabledElements.forEach((element) => {
			element.removeAttribute('tabindex');
			element.removeAttribute('data-temp-disabled');
		});
	}

	handleBackBtnClick() {
		const activeMegaMenu = document.querySelector<HTMLDivElement>(
			'.header-nav-menu__nav-item.active, .utility-nav-menu__nav-item.active'
		);

		if (null !== activeMegaMenu) {
			activeMegaMenu.classList.remove('active');
			this.closeMobileMegaMenu(activeMegaMenu);

			// Remove trap focus, re-enable nav elements, and return to mobile menu trap focus
			this.removeTrapFocus();
			this.enableAllNavElements();
			if (document.body.classList.contains('mobile-menu-open')) {
				this.trapMobileMenu();
			}
		}
	}

	handleMouseEnter(event: MouseEvent) {
		if (this.isMobile || this.isTouch) return;

		let target = event.currentTarget as HTMLElement;

		if (
			event.type === 'focusin' &&
			target.tagName === 'A' &&
			target.parentElement?.classList.contains('header-nav-menu__nav-item--has-dropdown')
		) {
			target = target.parentElement;
		}

		if (target.classList.contains('header-nav-menu__nav-item')) {
			this.showOverlay();
			document.body.classList.remove('pm-dropdown-open');
			document.body.classList.add('dropdown-open');

			this.profileMenus.forEach((menu) => {
				this.closeProfileMenu(menu);
			});
			this.openMegaMenu(target);
		}
		if (target.classList.contains('profile-menu-dropdown')) {
			this.openProfileMenu(target);
		}
	}

	handleMouseLeave(event: MouseEvent) {
		if (this.isMobile || this.isTouch) return;

		this.removeOverlay();
		document.body.classList.remove('pm-dropdown-open');
		document.body.classList.remove('dropdown-open');
		let target = event.currentTarget as HTMLElement;
		if (
			event.type === 'focusout' &&
			target.tagName === 'A' &&
			target.closest('.header-nav-menu__nav-item--depth-1')
		) {
			target = target.closest('.header-nav-menu__nav-item--depth-1') as HTMLElement;
		}
		if (target.classList.contains('header-nav-menu__nav-item')) {
			this.closeMegaMenu(target);
		}
		if (target.classList.contains('profile-menu-dropdown')) {
			this.closeProfileMenu(target);
		}
	}

	openProfileMenu(target: HTMLElement) {
		const pmDropdown = target.querySelector<HTMLElement>('.profile-menu-dropdown__menu');
		const trigger = target.querySelector<HTMLElement>('.profile-menu-dropdown__trigger');
		document.body.classList.add('pm-dropdown-open');
		target.classList.add('is-open');

		if (!trigger || !pmDropdown) return;

		pmDropdown?.classList.add('is-open');
		trigger.setAttribute('aria-pressed', 'true');

		const focusableElements = Array.from(
			target.querySelectorAll(this.focusableSelectors.join(','))
		) as HTMLElement[];

		const firstEl = focusableElements[0];
		const lastEl = focusableElements[focusableElements.length - 1];

		const trapFocus = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				this.closeProfileMenu(target);
				document.removeEventListener('keydown', trapFocus);
				trigger.focus();
				return;
			}

			if (e.key === 'Tab') {
				if (
					e.shiftKey &&
					firstEl.ownerDocument.activeElement === firstEl &&
					target.classList.contains('is-open')
				) {
					e.preventDefault();
					lastEl.focus();
				}
				if (
					!e.shiftKey &&
					lastEl.ownerDocument.activeElement === lastEl &&
					target.classList.contains('is-open')
				) {
					e.preventDefault();
					firstEl.focus();
				}
			}
		};

		target.addEventListener('keydown', trapFocus);

		// Trigger is the first focusable element
		// Focus the second focusable element after transition
		const focusAfterTransition = () => {
			if (focusableElements[1]) {
				focusableElements[1].focus();
			}
			pmDropdown.removeEventListener('transitionend', focusAfterTransition);
		};
		pmDropdown.addEventListener('transitionend', focusAfterTransition);
	}

	closeProfileMenu(target: HTMLElement) {
		const pmDropdown = target.querySelector<HTMLElement>('.profile-menu-dropdown__menu');
		const trigger = target.querySelector<HTMLElement>('.profile-menu-dropdown__trigger');
		document.body.classList.remove('pm-dropdown-open');
		target.classList.remove('is-open');

		if (!trigger || !pmDropdown) return;

		pmDropdown.classList.remove('is-open');
		trigger.setAttribute('aria-pressed', 'false');
	}

	handleProfileMenuClick(event: MouseEvent) {
		const target = event.target as HTMLElement;

		if (target.closest('.profile-menu-dropdown__menu')) {
			return;
		}

		const currentTarget = event.currentTarget as HTMLElement;

		if (currentTarget) {
			if (currentTarget.classList.contains('is-open')) {
				this.removeOverlay();
				this.closeProfileMenu(currentTarget);
			} else {
				this.showOverlay();
				this.openProfileMenu(currentTarget);
			}
		}
	}

	openMegaMenu(target: HTMLElement) {
		if (this.isMobile || this.isTouch) return;

		const megaMenu = target.querySelector<HTMLDivElement>(
			'.mm-dropdown, .header-nav-menu__dropdown'
		);
		const navLink = target.querySelector<HTMLAnchorElement>('.header-nav-menu__nav-link');
		if (megaMenu) {
			if (megaMenu && navLink) {
				navLink.classList.add('is-open');
				navLink.setAttribute('aria-expanded', 'true');
				megaMenu.classList.add('is-open');
			}
		}
	}

	closeMegaMenu(target: HTMLElement) {
		if (this.isMobile || this.isTouch) return;

		const megaMenu = target.querySelector<HTMLDivElement>(
			'.mm-dropdown, .header-nav-menu__dropdown'
		);
		const navLink = target.querySelector<HTMLAnchorElement>('.header-nav-menu__nav-link');
		if (megaMenu && navLink) {
			navLink.classList.remove('is-open');
			navLink.setAttribute('aria-expanded', 'false');
			megaMenu.classList.remove('is-open');
		}
	}

	handleMegaMenuClick(event: MouseEvent) {
		if (this.isMobile) {
			event.preventDefault();

			const target = event.currentTarget as HTMLAnchorElement;
			const parent = target.parentElement as HTMLDivElement;

			if (parent) {
				if (parent.classList.contains('active')) {
					parent.classList.remove('active');

					this.closeMobileMegaMenu(target, true);
				} else {
					parent.classList.add('active');

					this.openMobileMegaMenu(target, true);
				}
			}
		}
	}

	openMobileMegaMenu(item: HTMLAnchorElement | HTMLDivElement, isLink = false) {
		const li = isLink ? item.parentElement : item;
		if (!li) return;
		const megaMenu = li.querySelector<HTMLDivElement>(
			'.mm-dropdown, .header-nav-menu__dropdown, .utility-nav-menu__dropdown'
		);
		if (megaMenu) {
			megaMenu.classList.add('active');
			document.body.classList.add('mobile-mm-open');
			this.megaMenuBackBtns.forEach((btn) => {
				btn.classList.add('is-open');
			});

			// Set up trap focus for the mega menu
			const triggerElement = isLink ? (item as HTMLAnchorElement) : li.querySelector('a');
			if (triggerElement) {
				this.trapMegaMenu(megaMenu);
			}
		}
	}

	closeMobileMegaMenu(item: HTMLAnchorElement | HTMLDivElement, isLink = false) {
		const li = isLink ? item.parentElement : item;
		if (!li) return;
		const megaMenu = li.querySelector<HTMLDivElement>(
			'.mm-dropdown, .header-nav-menu__dropdown, .utility-nav-menu__dropdown'
		);
		if (megaMenu) {
			document.body.classList.add('mm-closing');
			megaMenu.classList.add('is-closing');
			megaMenu.classList.remove('active');
			this.megaMenuBackBtns.forEach((btn) => {
				btn.classList.remove('is-open');
			});
			document.body.classList.remove('mobile-mm-open');

			// Remove trap focus when closing mega menu and re-enable nav elements
			this.removeTrapFocus();
			this.enableAllNavElements();

			setTimeout(() => {
				document.body.classList.remove('mm-closing');
				megaMenu.classList.remove('is-closing');
			}, 300);
		}
	}

	setHeaderHeight() {
		if (!this.header) return;

		const headerHeight = this.header.offsetHeight;
		let headerHeightFull = this.header.offsetHeight;

		if (null !== this.adminBar) {
			headerHeightFull += this.adminBar.offsetHeight;
		}

		document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
		document.documentElement.style.setProperty('--headerHeight', headerHeight + 'px');
		document.documentElement.style.setProperty('--headerHeightFull', headerHeightFull + 'px');
	}
}

export default new MainHeader();
